import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import fpPromise from '@fingerprintjs/fingerprintjs';
import { BACKEND_URL } from '../utils/backendUrl';
import * as authUtils from '../utils/auth';
import { sanitizeEventInputForApi } from '../utils/planningIntent';
import {
  getInFlightPlanJob,
  saveInFlightPlanJob,
  clearInFlightPlanJob,
  getPendingPlanJobStart,
  savePendingPlanJobStart,
  clearPendingPlanJobStart
} from '../utils/asyncJobs';

const ThemeDetailsLoading = ({
  theme,
  themes,
  eventData,
  selectionPreferences,
  themePlans = {},
  currentPlanId,
  onDetailsLoaded,
  onCancel
}) => {
  const stepDurationMs = 1600;
  const hasInitiatedCall = useRef(false);
  const jobStartOwnerId = useRef(`plan_${Date.now()}_${Math.random().toString(36).slice(2)}`);
  const avgTimeSeconds = 8; // Typical completion time for the current plan builder
  const maxProgressBeforeComplete = 92; // Reserve 8% for actual completion

  const steps = useMemo(
    () => [
      { text: 'Building your structured plan', icon: '🛒' },
      { text: 'Sizing quantities for your guests', icon: '✨' },
      { text: 'Checking budget fit', icon: '💰' },
      { text: 'Packaging the shopping list', icon: '🎉' }
    ],
    []
  );

  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [doneSteps, setDoneSteps] = useState(() => steps.map(() => false));
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    if (!theme || !eventData) {
      onCancel?.();
      return;
    }

    const requestKey = JSON.stringify({
      themeId: theme.id,
      themeName: theme.name,
      themeDescription: theme.description,
      colorPalette: theme.color_palette,
      themeHighlights: theme.sample_items || theme.highlights || [],
      eventData,
      selectionPreferences: selectionPreferences || null
    });
    // Prevent duplicate calls in React StrictMode
    if (hasInitiatedCall.current) {
      return;
    }
    hasInitiatedCall.current = true;
    savePendingPlanJobStart({
      requestKey,
      theme,
      eventData,
      currentPlanId: currentPlanId || null,
      ownerId: jobStartOwnerId.current,
      createdAt: Date.now()
    });

    let isActive = true;
    let hasCalled = false; // Prevent double calls
    const startTime = Date.now();
    const minTotalTimeMs = 3500; // Keep successful fast runs from feeling intentional without overstating wait time.

    let apiResponse = null;
    let apiError = null;

    const tickIds = [];

    steps.slice(1).forEach((_, idx) => {
      const stepIndex = idx + 1;
      tickIds.push(
        setTimeout(() => {
          if (!isActive) return;

          setDoneSteps(prev => {
            const next = [...prev];
            next[stepIndex - 1] = true;
            return next;
          });

          setCurrentStep(stepIndex);
          const pct = Math.round((stepIndex / steps.length) * 80);
          setProgress(prev => Math.max(prev, pct));
        }, stepIndex * stepDurationMs)
      );
    });

    // Time-based progress calculation
    const updateProgress = () => {
      if (!isActive) return;
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setElapsedTime(elapsed);

      // Calculate progress based on elapsed time vs average time
      // Use a curve that slows down as it approaches maxProgressBeforeComplete
      const timeRatio = Math.min(elapsed / avgTimeSeconds, 1);

      // Use an easing function (ease-out cubic) for smoother progression
      // This makes progress faster at the start and slower near the end
      const easedRatio = 1 - Math.pow(1 - timeRatio, 3);

      // Map to progress range: 0 to maxProgressBeforeComplete
      const timeBasedProgress = easedRatio * maxProgressBeforeComplete;

      setProgress(prev => {
        // Use the maximum of step-based progress and time-based progress
        // This ensures progress never goes backwards
        return Math.max(prev, timeBasedProgress);
      });
    };

    // Update elapsed time and progress every second
    const timeInterval = setInterval(updateProgress, 1000);
    updateProgress(); // Initial call

    const finalize = (data) => {
      if (!isActive) return;

      setDoneSteps(steps.map(() => true));
      setProgress(100);

      // Merge details into theme
      const themeWithDetails = {
        ...theme,
        categories: data.categories,
        budget_guardrail: data.budget_guardrail,
        catering_suggestion: data.catering_suggestion
      };

      setTimeout(() => {
        if (!isActive) return;
        onDetailsLoaded(themeWithDetails);
      }, 700);
    };

    const fetchDetails = async () => {
      // Prevent double calls
      if (hasCalled) {
        console.warn('⚠️ ThemeDetailsLoading: fetchDetails already called, skipping duplicate call');
        return;
      }
      hasCalled = true;

      try {
        let visitorId = 'unknown';
        try {
          const fp = await fpPromise.load();
          const result = await fp.get();
          visitorId = result.visitorId;
        } catch (fpErr) {
          console.error('Failed to get fingerprint:', fpErr);
        }

        let headers = {
          'Content-Type': 'application/json',
          'X-Device-Fingerprint': visitorId,
          ...(await authUtils.getFreshAuthHeader())
        };

        const refreshHeaders = async () => {
          headers = {
            'Content-Type': 'application/json',
            'X-Device-Fingerprint': visitorId,
            ...(await authUtils.getFreshAuthHeader())
          };
          return headers;
        };

        const axiosWithAuthRetry = async (requestFn) => {
          try {
            return await requestFn(headers);
          } catch (error) {
            if (error?.response?.status === 401 && authUtils.getRefreshToken()) {
              const nextHeaders = await refreshHeaders();
              return requestFn(nextHeaders);
            }
            throw error;
          }
        };

        const cleanEventInput = sanitizeEventInputForApi(eventData);

        const requestPayload = {
          theme_id: theme.id,
          theme_name: theme.name,
          theme_description: theme.description,
          color_palette: theme.color_palette,
          theme_highlights: theme.sample_items || theme.highlights || [],
          event_input: cleanEventInput,
          theme_preferences: selectionPreferences,
          idempotency_key: requestKey
        };

        const waitForPendingJob = async () => {
          for (let attempt = 0; attempt < 30; attempt += 1) {
            const inFlight = getInFlightPlanJob();
            if (inFlight?.requestKey === requestKey && inFlight?.jobId) {
              return inFlight.jobId;
            }
            const pending = getPendingPlanJobStart();
            if (pending?.requestKey === requestKey && pending?.jobId) {
              return pending.jobId;
            }
            await new Promise((resolve) => setTimeout(resolve, 350));
          }
          return null;
        };

        const existingJob = getInFlightPlanJob();
        let jobId = existingJob?.requestKey === requestKey ? existingJob?.jobId : null;

        if (!jobId) {
          const pendingJob = getPendingPlanJobStart();
          if (pendingJob?.requestKey === requestKey && pendingJob?.ownerId !== jobStartOwnerId.current) {
            jobId = pendingJob.jobId || await waitForPendingJob();
          }
        }

        if (!jobId) {
          savePendingPlanJobStart({
            requestKey,
            theme,
            eventData,
            currentPlanId: currentPlanId || null,
            ownerId: jobStartOwnerId.current,
            createdAt: Date.now()
          });
          const startResponse = await axiosWithAuthRetry((authHeaders) => axios.post(
            `${BACKEND_URL}/api/jobs/plan-generation/start`,
            requestPayload,
            {
              timeout: 30000,
              headers: authHeaders
            }
          ));
          jobId = startResponse.data?.job_id;
          if (!jobId) {
            throw new Error('Plan generation job could not be started.');
          }
          savePendingPlanJobStart({ requestKey, jobId, createdAt: Date.now() });
          saveInFlightPlanJob({
            jobId,
            requestKey,
            theme,
            themes: Array.isArray(themes) ? themes : [],
            themePlans: themePlans && typeof themePlans === 'object' ? themePlans : {},
            eventData,
            selectionPreferences: selectionPreferences || null,
            currentPlanId: currentPlanId || null,
            createdAt: Date.now()
          });
          clearPendingPlanJobStart(requestKey);
        }

        while (isActive) {
          const pollResponse = await axiosWithAuthRetry((authHeaders) => axios.get(`${BACKEND_URL}/api/jobs/${jobId}`, {
            timeout: 30000,
            headers: authHeaders
          }));
          const job = pollResponse.data;

          if (job?.status === 'completed') {
            apiResponse = job.result;
            break;
          }

          if (job?.status === 'failed') {
            clearInFlightPlanJob();
            const detail = job?.error?.detail || 'Plan generation failed.';
            const statusCode = job?.error?.status_code || 500;
            const error = new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
            error.response = { status: statusCode, data: { detail } };
            throw error;
          }

          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      } catch (error) {
        clearPendingPlanJobStart(requestKey);
        apiError = error;
      }

      if (!isActive) return;

      if (apiError) {
        const detail = apiError.response?.data?.detail;
        const message = typeof detail === 'string'
          ? detail
          : detail?.message || apiError.message || 'Plan generation failed.';
        alert(`Error: ${message}. Please try again.`);
        onCancel();
        return;
      }

      if (!apiResponse?.success) {
        alert('Failed to load theme details. Please try again.');
        onCancel();
        return;
      }

      // Ensure minimum time for smooth UX (but don't delay if API is slow)
      const elapsed = Date.now() - startTime;
      const waitMs = Math.max(0, minTotalTimeMs - elapsed);
      setTimeout(() => finalize(apiResponse), waitMs);
    };

    fetchDetails();

    return () => {
      isActive = false;
      hasInitiatedCall.current = false; // Reset on unmount
      tickIds.forEach(id => clearTimeout(id));
      clearInterval(timeInterval);
    };
  }, [theme?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-xl mx-auto pt-4 sm:pt-16 pb-4 sm:pb-6 px-4 animate-slide-up">
      <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-8 text-center">
        {/* Theme Preview - visible at top on mobile */}
        <div className="mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-extrabold text-gray-900 mb-1">{theme.name}</h2>
          <p className="text-gray-500 text-xs sm:text-sm">{theme.description}</p>
        </div>

        {/* Color Palette Preview */}
        <div className="flex justify-center gap-1.5 mb-5">
          {theme.color_palette?.slice(0, 5).map((color, idx) => (
            <div
              key={idx}
              className="w-7 h-7 rounded-lg shadow-sm"
              style={{ backgroundColor: typeof color === 'string' ? color : color.hex }}
            />
          ))}
        </div>

        {/* Loading Animation */}
        <div className="mb-5">
          <div className="w-12 h-12 mx-auto mb-4 relative">
            <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
            <div
              className="absolute inset-0 border-4 border-primary rounded-full animate-spin"
              style={{
                borderTopColor: 'transparent',
                borderRightColor: 'transparent'
              }}
            ></div>
            <div className="absolute inset-0 flex items-center justify-center text-xl">
              {steps[currentStep].icon}
            </div>
          </div>

          <p className="text-sm sm:text-base font-semibold text-gray-800 mb-1.5">{steps[currentStep].text}...</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-purple-500 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-1.5">{Math.round(progress)}%</p>
          <p className="text-xs sm:text-sm text-gray-500 mt-1.5">
            ⏱️ Elapsed: {elapsedTime}s / Avg: ~8 seconds
          </p>
        </div>

        {/* Steps List */}
        <div className="space-y-1.5 text-left max-w-xs mx-auto">
          {steps.map((step, idx) => {
            const done = doneSteps[idx];
            const active = idx === currentStep;

            return (
              <div
                key={idx}
                className={`flex items-center gap-2.5 text-xs sm:text-sm transition-all duration-300 ${
                  done ? 'text-gray-800' : active ? 'text-gray-950 font-semibold' : 'text-gray-400'
                }`}
              >
                <span
                  className={`w-4 h-4 flex items-center justify-center rounded-full text-[10px] ${
                    done
                      ? 'bg-green-500 text-white'
                      : active
                        ? 'bg-primary text-white animate-pulse'
                        : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {done ? '✓' : idx + 1}
                </span>
                <span>{step.text}</span>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default ThemeDetailsLoading;
